<template>
    <Modal class="tiw__step__addToCart">
        <template #header>
            <p class="tiw__step__addToCart__header tiw__h2">
                <AwardIcon />
                {{ useTranslate("tiw__addToCart__title") }}
            </p>
            <p
                v-html="
                    useTranslate('tiw__addToCart__subtitle')
                        .replace('[[NUMBER]]', SuccessfulEvaluationStore.fields.phone)
                        .replace('[[TEXT]]', nextStepText)
                "
            ></p>
        </template>
        <p
            class="tiw__step__addToCart__info tiw__step__addToCart__info_mob tiw__text tiw_fs-12 tiw_usn"
        >
            <span>
                {{ useTranslate("tiw__addToCart__notContract") }}
            </span>
            <br />
            <Button color="link" variant="link" type="button" class="tiw_fs-12">
                <span>
                    {{ useTranslate("tiw__addToCart__sendAgain") }}
                </span>
            </Button>
        </p>

        <div class="tiw__step__addToCart__body">
            <!-- Чекбокс "Я подписал договор" -->
            <!-- <Checkbox v-model="SuccessfulEvaluationStore.fields.acceptContract">
                <span> Я подписал договор </span>
            </Checkbox> -->
            <template v-if="!isSigned">
                <div class="tiw__step__addToCart__warn">
                    {{ useTranslate("tiw__addToCart__warn") }}
                </div>
            </template>
            <div class="tiw__step__addToCart__actions">
                <button class="tiw__step__addToCart__back" @click="onBack">
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M6.47852 3.33325L2.00047 7.99992L6.47852 12.6666"
                            stroke="#808D9A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                        <line
                            x1="0.75"
                            y1="-0.75"
                            x2="11.072"
                            y2="-0.75"
                            transform="matrix(1 0 0 -1 2.17773 7.35449)"
                            stroke="#808D9A"
                            stroke-width="1.5"
                            stroke-linecap="round"
                        />
                    </svg>
                    <p>{{ useTranslate("tiw__back") }}</p>
                </button>
                <Button
                    type="button"
                    :disabled="loadingToNext"
                    :loading="loadingToNext"
                    class="tiw__step__addToCart__btn-send"
                    @click="onAddToCart"
                >
                    <span>
                        {{ nextStepText }}
                    </span>
                </Button>
            </div>
        </div>
        <p
            class="tiw__step__addToCart__info tiw__step__addToCart__info_desk tiw__text tiw_fs-12 tiw_usn"
        >
            <span>
                {{ useTranslate("tiw__addToCart__notContract") }}
            </span>
            <br />
            <Button
                color="link"
                variant="link"
                type="button"
                class="tiw_fs-12"
                :disabled="loadingToNext"
                @click="sendSmsAgain"
            >
                <span>
                    {{ useTranslate("tiw__addToCart__sendAgain") }}
                </span>
            </Button>
        </p>
    </Modal>
</template>

<script lang="ts">
export default {
    name: "AddToCart",
};
</script>

<script setup lang="ts">
/**
 * * 🅲🅾🅼🅿🅾🅽🅴🅽🆃 [AddToCart]
 * Модальное окно / экран "Ура! Все готово! Добавить в корзину"
 */

// Store
import { ref } from "vue";

// Components
import Modal from "@/components/modal/modal.vue";
// import Checkbox from "@/ui/checkbox/checkbox.vue";
import { BugTrackerSection, useBugTracker } from "@/helpers/bugTracker";
// Hooks
import { suck } from "@/helpers/consoleColors";
import parseStringPrice from "@/helpers/parseStringPrice";
import it4CheckContractSign from "@/hooks/requests/it4CheckContractSign/it4CheckContractSign";
import it4SendContractToSign from "@/hooks/requests/it4SendContractToSign/it4SendContractToSign";
import { useAnalyticsEventAddToCart } from "@/hooks/useAnalytics/useAnalytics";
import useStoreResetter from "@/hooks/useStoreResetter/useStoreResetter";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { useSuccessfulEvaluationStore } from "@/stores/successfulEvaluation/successfulEvaluation";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
// Icons
import AwardIcon from "@/ui/icons/award.vue";

const TIWStore = useTIWStore();
const ChooseDeviceStore = useChooseDeviceStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const SuccessfulEvaluationStore = useSuccessfulEvaluationStore();

const isSigned = ref<boolean>(true);
const loadingToNext = ref<boolean>(false);

const nextStepText = useTranslate(
    TIWStore.domainIsTakealot ? "tiw__continue" : "tiw__addToCart__addToCart",
);

const onAddToCart = async () => {
    loadingToNext.value = true;

    try {
        const ContractSignInfo = await it4CheckContractSign({
            token: TIWStore.WIDGET_DATA.token, // "d7bd115c-e845-4381-8f11-8044cbc11dfe",
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId, // deviceID сдаваемого устройства
        });

        // useBugTracker("AddToCart - it4CheckContractSign (response)", BugTrackerSection.INFO, {
        //     response: ContractSignInfo,
        // })

        if (ContractSignInfo.signed) {
            console.log("%cCONTRACT IS SIGNED", suck, ContractSignInfo.signId);

            useAnalyticsEventAddToCart();

            isSigned.value = true;

            const onCartData = {
                brand: ChooseDeviceStore.brandName,
                device_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
                device_imei: ChooseDeviceStore.field_imei.value,
                trade_up_model: TIWStore.INJECTED_GOOD?.panelTitle ?? null,
                trade_up_price: TIWStore.INJECTED_GOOD
                    ? String(parseStringPrice(TIWStore.INJECTED_GOOD.panelPrice))
                    : null,
                model: ChooseDeviceStore.deviceChain.join(" "),
                modelData: {
                    brand: ChooseDeviceStore.field_brand.value?.name || "",
                    category: ChooseDeviceStore.field_category.value?.name || "",
                    model: ChooseDeviceStore.field_model.value?.name || "",
                    other: ChooseDeviceStore.field_storage.value?.storage || "",
                    color: ChooseDeviceStore.field_color.value?.color || "",
                    code: ChooseDeviceStore?.code || "",
                },
                price: String(
                    parseStringPrice(HowYouRateDeviceStore.calculatedDeviceData.panelCost),
                ),
                trade_in_price: String(
                    parseStringPrice(
                        typeof HowYouRateDeviceStore.calculatedDeviceData.panelTradeInSale ===
                            "string"
                            ? HowYouRateDeviceStore.calculatedDeviceData.panelTradeInSale
                            : HowYouRateDeviceStore.calculatedDeviceData.panelTradeInSale
                                  .tradeInSale,
                    ),
                ),
                top_up_price: HowYouRateDeviceStore.calculatedDeviceData.panelActionSale
                    ? String(
                          parseStringPrice(
                              HowYouRateDeviceStore.calculatedDeviceData.panelActionSale.actionSale,
                          ),
                      )
                    : null,
                grade: HowYouRateDeviceStore.grade,
                certificate: ContractSignInfo.certificateNumber,
                signedDocumentUrl: ContractSignInfo.signedDocumentUrl,
                final_price: true,
                first_name: SuccessfulEvaluationStore.destructuredFio.firstName,
                last_name: SuccessfulEvaluationStore.destructuredFio.lastName,
                email: SuccessfulEvaluationStore.fields.email,
                phone: SuccessfulEvaluationStore.fields.phone,
                signingType: "TRUSTME_KZ",
            };

            TIWStore.INJECTED_TIW.current_api.onCart(onCartData);

            // useBugTracker("AddToCart - onCart (callback)", BugTrackerSection.INFO, {
            //     onCartData: onCartData,
            // })

            if (TIWStore.domainIsTakealot || TIWStore.domainIsAvoAfrica) {
                TIWStore.switchStep("thanksYou");
            } else {
                TIWStore.closeWidget();
                useStoreResetter();
            }
        } else {
            isSigned.value = false;
        }
    } catch (e) {
        useBugTracker("AddToCart - it4CheckContractSign", BugTrackerSection.ERROR, {
            exception: e.message,
            request: {
                token: TIWStore.WIDGET_DATA.token,
                deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            },
        });
    }

    loadingToNext.value = false;
};

const sendSmsAgain = async () => {
    loadingToNext.value = true;

    try {
        await it4SendContractToSign({
            token: TIWStore.WIDGET_DATA.token, // "d7bd115c-e845-4381-8f11-8044cbc11dfe",
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId, // deviceID сдаваемого устройства
            contractNumber: SuccessfulEvaluationStore.contractData.contractNumber, // Номер контракта
        });
    } catch (e) {
        useBugTracker("AddToCart - it4SendContractToSign", BugTrackerSection.ERROR, {
            exception: e.message,
            request: {
                token: TIWStore.WIDGET_DATA.token,
                deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
                contractNumber: SuccessfulEvaluationStore.contractData.contractNumber,
            },
        });
    }

    isSigned.value = true;
    loadingToNext.value = false;
};

const onBack = () => {
    TIWStore.switchStep(TIWStore.PREVIOUS_STEP);
};
</script>

<style lang="scss" src="./addToCart.scss"></style>
